import styled from "@emotion/styled";

import { MDSIconKind, MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentOpenTasksBadge } from "@/pages/agent/components/agent-open-tasks-badge";
import { AgentRowActionButton } from "@/pages/agent/components/agent-row-action-button";
import type { ProjectDetailViewModel } from "@/pages/agent/project/agent-project-page.utils";

export const AgentProjectDetailCard = ({
  projectDetail,
  onCommentProject,
  onOpenNextFollowUp,
}: {
  projectDetail: ProjectDetailViewModel;
  onCommentProject: ({ projectId }: { projectId: string }) => void;
  onOpenNextFollowUp: ({ followUpId }: { followUpId: string }) => void;
}) => {
  const projectTitle = projectDetail.project.title || "Untitled project";
  const description = projectDetail.project.description?.trim();
  const nextFollowUp = projectDetail.nextFollowUp;

  return (
    <StyledCard>
      <StyledCardHeader>
        {projectDetail.openTasksCount > 0 ? (
          <AgentOpenTasksBadge openTasksCount={projectDetail.openTasksCount} />
        ) : (
          <StyledBadgeSpacer />
        )}

        <AgentRowActionButton
          aria-label={`Comment on ${projectTitle}`}
          iconKind={MDSIconKind.Message}
          iconSize={14}
          size={28}
          onClick={() => {
            onCommentProject({ projectId: projectDetail.project.id });
          }}
        />
      </StyledCardHeader>

      <StyledTitle
        color={MDSTextColor.Primary}
        variant={MDSTextVariant.HeaderM}
        weight={MDSTextWeight.SemiBold}
      >
        {projectTitle}
      </StyledTitle>

      <StyledDivider />

      {description ? (
        <StyledDescription color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
          {description}
        </StyledDescription>
      ) : (
        <StyledDescription color={MDSTextColor.Tertiary} variant={MDSTextVariant.ParagraphS}>
          No description yet.
        </StyledDescription>
      )}

      <StyledNextFollowUpLine>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          Next follow-up:
        </MDSText>
        {nextFollowUp ? (
          <AgentLinkButton
            onClick={() => {
              onOpenNextFollowUp({ followUpId: nextFollowUp.id });
            }}
          >
            {nextFollowUp.content || "Untitled follow-up"}
          </AgentLinkButton>
        ) : (
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
            None scheduled
          </MDSText>
        )}
      </StyledNextFollowUpLine>
    </StyledCard>
  );
};

const StyledCard = styled.article`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[5]};
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledBadgeSpacer = styled.span`
  flex: 1 1 auto;
`;

const StyledTitle = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledDivider = styled.div`
  background: ${({ theme }) => theme.colors.border.surface};
  height: 1px;
  width: 100%;
`;

const StyledDescription = styled(MDSText)`
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const StyledNextFollowUpLine = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;
